import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import _NumberContainer from "Timer-NumberContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=NumberContainer";
import _NumberHalf from "Timer-NumberHalf.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=NumberHalf";
import _NumberText from "Timer-NumberText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=NumberText";
import _Group from "Timer-Group.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=Group";
import _Indicator from "Timer-Indicator.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=Indicator";
import _StaticNumberContainer from "Timer-StaticNumberContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=StaticNumberContainer";
import _TimerContainer from "Timer-TimerContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/unibrows/seasonal_discount_unibrow/Timer.tsx?styleId=TimerContainer";


type FigureProps = {
  last: number;
  number: number;
  mini: boolean;
  darkDigits?: boolean;
};

type Props = {
  // Allow external styling
  className?: string;
  // Number of seconds left on the timer
  startDuration: number;
  // Only show minutes and seconds if hours are '00'
  hideHours?: boolean;
  mini?: boolean;
  style?: object;
  darkIndicator?: boolean;
  darkDigits?: boolean;
};

const NumberContainer = /*#__PURE__*/styled("div", null, {
  displayName: "NumberContainer",
  styles: _NumberContainer
});

const NumberHalf = /*#__PURE__*/styled("div", null, {
  displayName: "NumberHalf",
  styles: _NumberHalf
});

const NumberText = /*#__PURE__*/styled("div", null, {
  displayName: "NumberText",
  styles: _NumberText
});

const Group = /*#__PURE__*/styled("div", null, {
  displayName: "Group",
  styles: _Group
});

const Indicator = /*#__PURE__*/styled("p", null, {
  displayName: "Indicator",
  styles: _Indicator
});

const StaticNumberContainer = /*#__PURE__*/styled("div", null, {
  displayName: "StaticNumberContainer",
  styles: _StaticNumberContainer
});

const TimerContainer = /*#__PURE__*/styled("div", null, {
  displayName: "TimerContainer",
  styles: _TimerContainer
});

const DURATION = 500;

const ROTATE_KEYFRAMES = [
  { transform: 'rotateX(0)' },
  { transform: 'rotateX(-180deg)' },
];

/**
 * Return the individual digits for hours, minutes, and numbers
 * given the duration in seconds.
 */
const getDigitsFromDuration = (duration: number): number[] => {
  const hours = Math.floor(duration / 3600);
  let time = duration - hours * 3600;
  const mins = Math.floor(time / 60);
  time = time - mins * 60;
  const secs = time % 60;

  const hs = String(hours).split('').map(Number);
  const ms = String(mins).split('').map(Number);
  const ss = String(secs).split('').map(Number);

  // If we only have one number, we are at single digits
  // in that place and we need to add a leading 0
  if (hs.length === 1) hs.unshift(0);
  if (ms.length === 1) ms.unshift(0);
  if (ss.length === 1) ss.unshift(0);

  return [...hs, ...ms, ...ss];
};

// Safari doesn't support `backface-visibility`, it needs
// `-webkit-backface-visibility`.
// Safari has an obnoxious flickering bug that affects the whole page
// because it is a bad browser that can't handle transform animations correctly.
// `-webkit-backface-visibility: hidden` in the right spots
// fixes the flickering bug, but this animation needs the backface
// to be visibile on certain elements to look correct.
// This just makes sure we only animate in good browsers that don't suck.
// SSR-safe: Check if CSS is defined before using it (not available on server)
const useAnimation =
  typeof CSS !== 'undefined' && CSS.supports('backface-visibility: hidden');

// `last` is needed to show the correct number on the front,
// `number` is the number on the back side of the cards
// Using React.PureComponent here since we rerender the timer every second,
// but usually only one or two digits change at a time,
// so this should help with performance.
// Initially, this was a function component, but due to the way
// useEffect/useState interact, there was a pretty consistent jitter every render
// as the new number would show very briefly, then it would switch to the `last`
// number, animate, then show the new number again, before repeating on the next
// tick of the timer.
// Using a class component with componentDidUpdate is uglier, but gives us finer
// control over when state updates, and allows us to avoid those "inbetween" renders
// that were causing the flickering.
class AnimatedFigure extends React.PureComponent<FigureProps> {
  topRef: HTMLDivElement | null = null;
  topBackRef: HTMLDivElement | null = null;

  state = {
    useNext: false,
  };

  componentDidUpdate(prevProps: FigureProps) {
    const { number } = this.props;
    const { number: prevNumber } = prevProps;

    if (prevNumber === number) return;

    this.setState({ useNext: false }, () => {
      const topAnim = this.topRef?.animate(ROTATE_KEYFRAMES, {
        duration: DURATION,
        easing: 'ease-in-out',
      }).finished;
      const topBackAnim = this.topBackRef?.animate(ROTATE_KEYFRAMES, {
        duration: DURATION,
        easing: 'ease-in-out',
      }).finished;

      Promise.all([topAnim, topBackAnim]).then(() => {
        this.setState({ useNext: true });
      });
    });
  }

  render() {
    const { last, number, mini, darkDigits = false } = this.props;
    const { useNext } = this.state;

    return (
      <NumberContainer mini={mini} darkDigits={darkDigits}>
        {/* These two halves are the ones that flip */}
        <NumberHalf ref={(el) => (this.topRef = el)} top>
          <NumberText top>{useNext ? number : last}</NumberText>
        </NumberHalf>
        <NumberHalf ref={(el) => (this.topBackRef = el)} topBack>
          <NumberText topBack>{number}</NumberText>
        </NumberHalf>

        {/* These don't flip */}
        <NumberHalf topBehind>
          <NumberText top>{number}</NumberText>
        </NumberHalf>
        <NumberHalf bottom>
          <NumberText bottom>{useNext ? number : last}</NumberText>
        </NumberHalf>
      </NumberContainer>
    );
  }
}

const StaticFigure = ({ number, darkDigits = false }: FigureProps) => (
  <StaticNumberContainer darkDigits={darkDigits}>
    {number}
  </StaticNumberContainer>
);

const Figure = useAnimation ? AnimatedFigure : StaticFigure;

const Timer: React.FC<Props> = ({
  className,
  startDuration,
  hideHours,
  mini,
  style,
  darkIndicator,
  darkDigits = false,
}) => {
  // Because of the way the animation works, we actually want to start
  // one second sooner.
  // We're dealing with a discount campaign here, not nuclear reactors,
  // so I don't think being off by a second is that big of a deal.
  const [duration, setDuration] = useState(startDuration - 1);

  // If our startDuration changes, we want to reset the timer
  useEffect(() => {
    setDuration(startDuration);
  }, [startDuration]);

  useEffect(() => {
    const id = setInterval(() => {
      setDuration((d) => {
        if (d === 0) {
          clearInterval(id);
          return d;
        }

        return d - 1;
      });
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, []);

  const [h1, h2, m1, m2, s1, s2] = getDigitsFromDuration(duration);
  const [h1last, h2last, m1last, m2last, s1last, s2last] =
    getDigitsFromDuration(duration + 1);

  // Hide hours when all hour digits are zero and hideHours is true
  const hideHoursFromTimer = !!(hideHours && !h1 && !h2 && !h1last && !h2last);

  return (
    <TimerContainer
      className={className}
      withoutHours={hideHoursFromTimer}
      mini={!!mini}
      style={{ ...style }}
    >
      {!hideHoursFromTimer && (
        <Group>
          <Figure
            last={h1last}
            number={h1}
            mini={!!mini}
            darkDigits={darkDigits}
          />
          <Figure
            last={h2last}
            number={h2}
            mini={!!mini}
            darkDigits={darkDigits}
          />
        </Group>
      )}
      <Group>
        <Figure
          last={m1last}
          number={m1}
          mini={!!mini}
          darkDigits={darkDigits}
        />
        <Figure
          last={m2last}
          number={m2}
          mini={!!mini}
          darkDigits={darkDigits}
        />
      </Group>
      <Group>
        <Figure
          last={s1last}
          number={s1}
          mini={!!mini}
          darkDigits={darkDigits}
        />
        <Figure
          last={s2last}
          number={s2}
          mini={!!mini}
          darkDigits={darkDigits}
        />
      </Group>

      {!hideHoursFromTimer && (
        <Indicator mini={!!mini} dark={!!darkIndicator}>
          Hours
        </Indicator>
      )}
      <Indicator mini={!!mini} dark={!!darkIndicator}>
        Minutes
      </Indicator>
      <Indicator mini={!!mini} dark={!!darkIndicator}>
        Seconds
      </Indicator>
    </TimerContainer>
  );
};

export default Timer;
